import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Captions } from 'lucide-react';

/**
 * Shared caption-line list editor — used by BOTH the Clip Editor
 * (`clip.captions`) and the Main Editor's caption-block text items
 * (`item.text.segments`, via EditorCaptionControls), so there is one
 * editing engine for captions app-wide. Timestamps are item/clip-relative
 * (0 = that item's/clip's own start), so they stay correct no matter how
 * the owning item has been trimmed/split/moved — no re-timing math needed
 * on save.
 */
export function EditorCaptionsPanel({ captions, duration, currentTime = 0, onAdd, onUpdate, onRemove }) {
  if (!captions) {
    return <p className="text-sm text-[#8A93A6] text-center py-6" data-testid="captions-panel-empty">Select a clip to edit captions.</p>;
  }
  const dur = Math.max(0.1, duration || 0.1);

  return (
    <div className="space-y-3" data-testid="editor-captions-panel">
      <Button
        size="sm" variant="outline" className="w-full border-white/10"
        onClick={() => onAdd(Math.min(currentTime, dur - 0.5))}
        data-testid="add-caption-button"
      >
        <Plus className="h-4 w-4 mr-2" />Add Caption at Current Time
      </Button>

      {captions.length === 0 && (
        <p className="text-sm text-[#8A93A6] text-center py-4" data-testid="captions-list-empty">
          <Captions className="h-5 w-5 mx-auto mb-1 opacity-50" />No captions yet.
        </p>
      )}

      <div className="space-y-2 max-h-80 overflow-y-auto">
        {captions.map((cap) => (
          <div key={cap.id} className="p-2 rounded-lg bg-white/5 border border-white/10 space-y-1.5" data-testid={`caption-row-${cap.id}`}>
            <div className="flex items-center gap-2">
              <Input
                type="number" step="0.1" value={cap.start.toFixed(1)}
                onChange={(e) => onUpdate(cap.id, { start: Math.min(Number(e.target.value), cap.end - 0.1) })}
                className="h-7 w-20 bg-[#1C1F26] border-white/10 text-white text-xs" data-testid={`caption-start-${cap.id}`}
              />
              <span className="text-[#8A93A6] text-xs">to</span>
              <Input
                type="number" step="0.1" value={cap.end.toFixed(1)}
                onChange={(e) => onUpdate(cap.id, { end: Math.max(Number(e.target.value), cap.start + 0.1) })}
                className="h-7 w-20 bg-[#1C1F26] border-white/10 text-white text-xs" data-testid={`caption-end-${cap.id}`}
              />
              <button onClick={() => onRemove(cap.id)} className="ml-auto text-red-400 hover:text-red-300" data-testid={`caption-remove-${cap.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <Input
              value={cap.text}
              onChange={(e) => onUpdate(cap.id, { text: e.target.value })}
              placeholder="Caption text"
              className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid={`caption-text-${cap.id}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
